import React, { useMemo, useState, useEffect, useRef } from 'react';
import { useGetDealerDropdownListQuery } from '../../api/endpoints/dealer';
import { useLanguage } from '../../contexts/LanguageContext';
import { DomesticOrForeignEnum } from '../../utils/DomesticOrForeignEnum';



const DealerListBox = ({
    value = [],
    onChange = () => {},
    visible = true,
    className = '',
    disabled = false,
    placeholder = 'Bayi ara...',
    rowCount = 100,
    managerUserId = null,
    dealerType = null,
    domesticOrForeign = null 
}) => {
    const { th } = useLanguage();

    const [searchText, setSearchText] = useState('');

    const effectiveRowCount = domesticOrForeign === DomesticOrForeignEnum.Domestic ? 500 : rowCount;

    const { data: resp, isLoading } = useGetDealerDropdownListQuery({
        RowCount: effectiveRowCount,
        SearchText: searchText,
        ManagerUserId: managerUserId,
        DealerType: dealerType,
        DomesticOrForeign: domesticOrForeign
    });

    const apiItems = useMemo(() => (Array.isArray(resp) ? resp : resp?.items ?? []), [resp]);

    const fetched = useMemo(() => {
        return apiItems
            .map((d) => {
                const id = Number(d?.dealerId ?? d?.erpDealerId ?? d?.id);
                if (!Number.isFinite(id)) return null;
                const name = d?.dealerName ?? d?.name ?? '';
                return { id, displayValue: name || String(id) };
            })
            .filter(Boolean);
    }, [apiItems]);

    const valueIds = useMemo(() => (value || []).map(Number).filter(Number.isFinite), [value]);

    // Seçili bayilerin tam bilgisini tut (listeden çıksa bile görünsün)
    const selectedOptionsMap = useRef(new Map());
    useEffect(() => {
        fetched.forEach((o) => {
            if (valueIds.includes(o.id)) selectedOptionsMap.current.set(o.id, o);
        });
        // Seçimden çıkarılanları map'ten sil
        selectedOptionsMap.current.forEach((_, id) => {
            if (!valueIds.includes(id)) selectedOptionsMap.current.delete(id);
        });
    }, [valueIds, fetched]);

    // Tüm options: seçililer üstte, gerisini fetched'den al (duplicate olmadan)
    const options = useMemo(() => {
        const selectedOpts = valueIds.map((id) => selectedOptionsMap.current.get(id) ?? fetched.find((f) => f.id === id)).filter(Boolean);

        const unselected = fetched.filter((f) => !valueIds.includes(f.id));

        // Arama varsa seçili + arama sonuçları
        const list = searchText.trim() ? [...selectedOpts, ...unselected] : [...selectedOpts, ...unselected];

        // duplicate guard
        const seen = new Set();
        return list.filter((o) => {
            if (seen.has(o.id)) return false;
            seen.add(o.id);
            return true;
        });
    }, [fetched, valueIds, searchText]);

    // Tümünü seç
    const handleSelectAll = () => {
        const allIds = fetched.map((o) => o.id);
        const allSelected = allIds.every((id) => valueIds.includes(id));
        if (allSelected) {
            onChange([]);
        } else {
            onChange(allIds);
        }
    };

    const allSelected = fetched.length > 0 && fetched.every((o) => valueIds.includes(o.id));

    // Dialog kapanınca seçimleri temizle
    useEffect(() => {
        if (!visible && valueIds.length) onChange([]);
    }, [visible]);

    const handleToggle = (id) => {
        if (valueIds.includes(id)) {
            onChange(valueIds.filter((v) => v !== id));
        } else {
            onChange([...valueIds, id]);
        }
    };

    return (
        <div className={`dealer-listbox-wrapper ${className}`} style={{ border: '1px solid #d1d5db', borderRadius: 6, overflow: 'hidden' }}>
            {/* Arama */}
            <div style={{ padding: '8px', borderBottom: '1px solid #e5e7eb', backgroundColor: '#f9fafb' }}>
                <span className="p-input-icon-left" style={{ width: '100%' }}>
                    <i className="pi pi-search" style={{ left: '10px' }} />
                    <input
                        type="text"
                        value={searchText}
                        onChange={(e) => setSearchText(e.target.value)}
                        placeholder={placeholder}
                        disabled={disabled}
                        style={{
                            width: '100%',
                            padding: '6px 10px 6px 32px',
                            border: '1px solid #d1d5db',
                            borderRadius: 4,
                            fontSize: '0.875rem',
                            outline: 'none'
                        }}
                    />
                </span>
            </div>

            {/* Tümünü Seç */}
            {!searchText.trim() && (
                <div
                    onClick={!disabled ? handleSelectAll : undefined}
                    style={{
                        padding: '8px 12px',
                        borderBottom: '1px solid #e5e7eb',
                        cursor: disabled ? 'default' : 'pointer',
                        backgroundColor: allSelected ? '#1d6fa4' : '#eef6fc',
                        color: allSelected ? '#fff' : '#1d6fa4',
                        fontWeight: 600,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 8,
                        userSelect: 'none'
                    }}
                >
                    <i className={`pi ${allSelected ? 'pi-check-square' : 'pi-stop'}`} />
                    {th('announcement.formLabels.selectAll')} ({fetched.length})
                </div>
            )}

            {/* Liste */}
            <div style={{ height: 270, overflowY: 'auto' }}>
                {isLoading ? (
                    <div style={{ padding: 16, textAlign: 'center', color: '#6b7280' }}>
                        <i className="pi pi-spin pi-spinner mr-2" />
                        Yükleniyor...
                    </div>
                ) : options.length === 0 ? (
                    <div style={{ padding: 16, textAlign: 'center', color: '#6b7280' }}>{searchText ? 'Sonuç yok' : 'Bayi bulunamadı'}</div>
                ) : (
                    options.map((opt, idx) => {
                        const isSelected = valueIds.includes(opt.id);
                        const isLast = idx === valueIds.length - 1 && valueIds.length > 0 && !searchText.trim();
                        return (
                            <React.Fragment key={opt.id}>
                                <div
                                    onClick={!disabled ? () => handleToggle(opt.id) : undefined}
                                    style={{
                                        padding: '8px 12px',
                                        cursor: disabled ? 'default' : 'pointer',
                                        backgroundColor: isSelected ? '#c3ddf3' : '#fff',
                                        color: isSelected ? '#052033' : '#1f2937',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 8,
                                        userSelect: 'none',
                                        transition: 'background 0.15s'
                                    }}
                                    onMouseEnter={(e) => {
                                        if (!isSelected) e.currentTarget.style.backgroundColor = '#f3f4f6';
                                    }}
                                    onMouseLeave={(e) => {
                                        e.currentTarget.style.backgroundColor = isSelected ? '#c3ddf3' : '#fff';
                                    }}
                                >
                                    <i className={`pi ${isSelected ? 'pi-check-circle' : 'pi-circle'}`} style={{ color: isSelected ? '#1d6fa4' : '#9ca3af', fontSize: '1rem' }} />
                                    <span style={{ fontSize: '0.875rem' }}>{opt.displayValue}</span>
                                </div>
                                {/* Seçili ve seçisiz arasına ayırıcı */}
                                {isLast && <div style={{ borderTop: '2px dashed #93c5fd', margin: '2px 0' }} />}
                            </React.Fragment>
                        );
                    })
                )}
            </div>

            {/* Alt bilgi */}
            {valueIds.length > 0 && (
                <div
                    style={{
                        padding: '6px 12px',
                        borderTop: '1px solid #e5e7eb',
                        backgroundColor: '#eff6ff',
                        fontSize: '0.8rem',
                        color: '#1d6fa4',
                        fontWeight: 500
                    }}
                >
                    <i className="pi pi-check-circle mr-1" />
                    {valueIds.length} bayi seçildi
                </div>
            )}
        </div>
    );
};

export default DealerListBox;
